// ============================================================================
// AccountInactivePage - Shown when student account is deactivated
// ============================================================================

import React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
} from "@mui/material";
import {
  LockOutlined as LockIcon,
  Email as EmailIcon,
  Logout as LogoutIcon,
  School as SchoolIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { StudentProfile } from "../../utils/studentUtils";

const AccountInactivePage: React.FC = () => {
  const { user, signOut } = useAuth();
  const profile = user as StudentProfile | null;

  // Get teacher info if available
  const teacher = profile?.myClassroom?.teacher;
  const studentName = profile
    ? `${profile.firstName} ${profile.lastName}`
    : "Student";

  // Build mailto link with pre-filled content
  const buildMailtoLink = () => {
    if (!teacher?.email) {
      return "mailto:support@eapspace.com?subject=Account%20Reactivation%20Request";
    }

    const subject = encodeURIComponent(
      "Account Reactivation Request - EAP Space"
    );
    const body = encodeURIComponent(
      `Dear ${teacher.name || "Teacher"},\n\n` +
        `I am writing to request the reactivation of my EAP Space account.\n\n` +
        `Student Name: ${studentName}\n` +
        `Email: ${profile?.email || "N/A"}\n\n` +
        `Please let me know if you need any additional information.\n\n` +
        `Thank you,\n${studentName}`
    );

    return `mailto:${teacher.email}?subject=${subject}&body=${body}`;
  };

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "error.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: "error.main" }} />
        </Box>

        {/* Title */}
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Account Inactive
        </Typography>

        {/* Message */}
        <Typography variant="body1" color="text.secondary" paragraph>
          Your EAP Space account has been temporarily deactivated. This may be
          due to administrative reasons or a request from your teacher.
        </Typography>

        <Alert severity="info" sx={{ my: 3, textAlign: "left" }}>
          <Typography variant="body2">
            <strong>What does this mean?</strong>
            <br />
            While your account is inactive, you cannot access course materials
            or quizzes. Your progress has been saved and will be available once
            your account is reactivated.
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* Teacher Contact Card */}
        {teacher ? (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SchoolIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="subtitle1" fontWeight={500}>
                  Contact Your Teacher
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {teacher.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teacher.email}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                No teacher contact information available.
                <br />
                Please contact support at{" "}
                <a href="mailto:support@eapspace.com">support@eapspace.com</a>
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            href={buildMailtoLink()}
            fullWidth
          >
            Request Reactivation
          </Button>

          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            fullWidth
          >
            Sign Out
          </Button>
        </Box>

        {/* Footer Note */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 3 }}
        >
          If you believe this is an error, please contact your teacher or
          administrator.
        </Typography>
      </Paper>
    </Container>
  );
};

export default AccountInactivePage;
